import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { getAuthMe, postAuthDisconnectMailbox } from '@/api/generated/auth/auth';
import { Button } from '@/components/ui/button';
import { queryKeys } from '@/config/query/queryKeys';
import { useLucioAccountEmail } from '@/features/auth/hooks/useLucioAccountEmail';
import { ROUTES } from '@/utils/routeUtils';

import { DisconnectMailboxDialog } from './DisconnectMailboxDialog';

/**
 * Mailbox-connection management for the Profile page. The mailbox connection
 * (the stored Graph credential driving background sync and automations) is
 * independent of the login session: logging out keeps it, and this card is
 * the one place to explicitly disconnect or reconnect it.
 */
interface MailboxConnectionCardProps {
  onLogoutClick: () => void;
}

// Cognitive score is JSX nesting on a status row + two buttons, not logic.
// fallow-ignore-next-line complexity
export function MailboxConnectionCard({ onLogoutClick }: MailboxConnectionCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showConfirm, setShowConfirm] = useState(false);
  const lucioAccountEmail = useLucioAccountEmail();

  const { data: me } = useQuery({
    queryFn: () => getAuthMe(),
    queryKey: queryKeys.auth.session(),
    staleTime: 30_000,
  });

  const disconnectMutation = useMutation({
    mutationFn: () => postAuthDisconnectMailbox(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.auth.all });
      setShowConfirm(false);
      // Without a Graph credential the app can't do anything mailbox-backed —
      // land on the Connect Mailbox screen (which now shows reconnect copy).
      navigate(ROUTES.CONNECT_MAILBOX, { replace: true });
    },
  });

  if (!me?.mailboxConnected) {
    return null;
  }

  const active = me.graphTokenActive;
  const email = me.user?.email ?? '';

  return (
    <>
      <div className="flex w-full items-center justify-between gap-2">
        <div className="flex min-w-0 flex-col">
          <p className="text-tertiary m-0 p-0 font-['Inter'] text-[11px] leading-[160%] font-medium">
            {t('profile.account.title')}
          </p>
          <p className="text-primary m-0 truncate p-0 font-['Inter'] text-[12px] leading-[160%] font-medium">
            {lucioAccountEmail ?? '—'}
          </p>
        </div>
        <Button
          aria-label={t('profile.logout.buttonAria')}
          className="h-7 shrink-0 rounded-md border border-[#E0E0E0] bg-white px-2.5 text-xs text-[#333333] hover:bg-gray-50"
          onClick={onLogoutClick}
          size="sm"
          variant="outline"
        >
          {t('profile.logout.buttonAlt')}
        </Button>
      </div>
      <div className="flex w-full items-center justify-between gap-2">
        <div className="flex min-w-0 flex-col">
          <p className="text-tertiary m-0 p-0 font-['Inter'] text-[11px] leading-[160%] font-medium">
            {t('profile.mailbox.title')}
          </p>
          <div className="flex min-w-0 items-center gap-1.5">
            <span
              aria-hidden
              className={`h-1.5 w-1.5 shrink-0 rounded-full ${active ? 'bg-emerald-500' : 'bg-amber-500'}`}
            />
            <p className="text-primary m-0 truncate p-0 font-['Inter'] text-[12px] leading-[160%] font-medium">
              {email}
            </p>
            <span className="text-tertiary shrink-0 font-['Inter'] text-[11px] leading-[160%]">
              {active ? t('profile.mailbox.connected') : t('profile.mailbox.reconnectRequired')}
            </span>
          </div>
        </div>

        {active ? (
          <Button
            className="h-7 shrink-0 rounded-md border border-[#E0E0E0] bg-white px-2.5 text-xs text-[#333333] hover:bg-gray-50"
            onClick={() => setShowConfirm(true)}
            size="sm"
            variant="outline"
          >
            {t('profile.mailbox.disconnect')}
          </Button>
        ) : (
          <Button
            className="h-7 shrink-0 rounded-md bg-black px-2.5 text-xs text-white hover:bg-black hover:opacity-90"
            onClick={() => navigate(ROUTES.CONNECT_MAILBOX)}
            size="sm"
          >
            {t('profile.mailbox.reconnect')}
          </Button>
        )}
      </div>

      <DisconnectMailboxDialog
        email={email}
        isPending={disconnectMutation.isPending}
        onConfirm={() => disconnectMutation.mutate()}
        onOpenChange={setShowConfirm}
        open={showConfirm}
      />
    </>
  );
}
